/**
 * L'accord réduit à ce qu'on en dit quand on le prend : un nom par note, et le
 * doigt de chaque main dessous.
 *
 * Pur : ni React, ni DOM. C'est le modèle de l'écran du parcours
 * fermé, où l'on ne joue pas des notes mais où l'on prend un accord.
 *
 * ## Une colonne par classe de hauteur, pas une par touche
 *
 * `Ré · Fa · La` a trois colonnes, et l'accord a quatre touches : la
 * fondamentale est sous les deux mains. Nommer `Ré` deux fois dirait qu'il y a
 * quatre notes dans l'accord, ce qui est faux ; c'est la ligne des doigtés qui
 * dit que deux mains la prennent, et c'est exactement ce qu'elle est là pour
 * dire.
 *
 * ## L'ordre est celui de la main droite
 *
 * Il a d'abord été celui des touches, de la plus grave à la plus aiguë, sur
 * l'argument que la basse réelle décide de la couleur qu'on entend. C'était
 * vrai et inutilisable : aux niveaux fabriqués la gauche tient la fondamentale
 * sous tout, donc la ligne s'ouvrait sur la fondamentale quel que soit le
 * renversement, et le renversement est justement ce que ces niveaux enseignent.
 * *Sol mineur, deuxième renversement* s'écrivait `Sol · Ré · Sib` au lieu de
 * `Ré · Sol · Sib`.
 *
 * La ligne suit donc la pile de la **main droite**, celle qui porte le
 * renversement, et c'est aussi la seule que le joueur construit.
 *
 * ## La main gauche n'ouvre plus la ligne
 *
 * Une classe que la droite ne jouait pas ouvrait la ligne, au motif qu'elle est
 * sous la pile. La raison tenait tant que la gauche tenait la **fondamentale** :
 * elle n'ajoutait alors aucun nom, puisque la droite jouait déjà cette
 * classe-là, et la règle ne se voyait nulle part.
 *
 * Depuis la basse publiée, la gauche joue la ligne écrite, qui traverse des
 * notes de passage : sur 23,9 % des fenêtres du catalogue elle atteint au moins
 * une hauteur qui n'est pas dans l'accord. La ligne annonçait donc comme note
 * de l'accord une note qui n'en est pas une, et *si bémol majeur, premier
 * renversement* s'écrivait `Do · Ré · Fa · Si♭` : quatre noms pour une triade,
 * dont deux venus de la basse.
 *
 * La ligne est donc exactement la pile de la droite. Ce que la gauche joue se
 * lit à côté et non dedans : la basse, à gauche derrière un séparateur, et le
 * rail de rythme pour son dessin. Le champ `left` ne change pas de rôle pour
 * autant : quand les deux mains prennent la même classe, le doigté de la gauche
 * reste sous sa colonne, où il dit ce qu'il a toujours dit.
 *
 * ## Le doigté se lit sur les prises, il ne se calcule pas ici
 *
 * L'analyseur fait tourner un solveur, et il publie les prises **du niveau**,
 * pas celles de la partition. Écrire une table de doigtés de ce côté-ci en
 * ferait une seconde réponse, plus faible, à une question déjà tranchée. Quand
 * l'analyse ne porte pas de prise, le chiffre est absent, et absent est une
 * réponse honnête.
 */

import type { PhraseGrip } from "@/lib/learning-journey/types";
import { windowToneName } from "@/lib/music/theory-helpers";

/** Ce qu'une main fait sur une note de l'accord. */
export type ChordNoteHand = {
  readonly midi: number;
  /** Le doigt que l'analyseur a posé dessus, ou `null` s'il n'en a posé aucun. */
  readonly finger: number | null;
};

/** Une note de l'accord, nommée, avec ce que chaque main en fait. */
export type ChordNoteColumn = {
  readonly pitchClass: number;
  /**
   * C'est la fondamentale de l'accord.
   *
   * Elle s'accentue à l'affichage, comme sur l'écran d'exercice d'accords :
   * c'est la note qui donne son nom à l'accord, et la seule dont la place dans
   * la main change ce qu'on entend.
   */
  readonly root: boolean;
  /**
   * Le nom, dans l'orthographe de l'accord et **pas** dans la notation du
   * joueur : c'est `useNoteLabel` qui la connaît, et personne ici ne réécrit
   * la convention.
   */
  readonly name: string;
  /** Les touches que cette colonne demande, toutes mains confondues, croissantes. */
  readonly midis: readonly number[];
  /**
   * Ce que la gauche fait sur **cette note-là**, ou `null` quand elle est
   * ailleurs.
   *
   * Elle ne fabrique jamais de colonne : la ligne est la pile de la droite. Ce
   * champ dit seulement que les deux mains prennent la même classe, ce qui est
   * exactement ce que la ligne des doigtés est là pour montrer.
   */
  readonly left: ChordNoteHand | null;
  readonly right: ChordNoteHand | null;
};

const NO_FINGERS: ReadonlyMap<number, number> = new Map();

/**
 * Le doigté d'une main sur une fenêtre d'accord, lu sur ses prises.
 *
 * La première prise qui chevauche la fenêtre, et pas celle qui commence
 * dedans : une prise est par définition une durée pendant laquelle aucun doigt
 * ne change de touche, donc son doigté vaut sur toute la fenêtre qu'elle
 * couvre, y compris quand elle a commencé à l'accord d'avant.
 *
 * Deux doigts peuvent partager une touche, ce qui est une vraie technique et
 * non un défaut de données. La colonne n'a la place que d'un chiffre : c'est le
 * premier qui reste, et la substitution ne se dit pas sur cet écran.
 */
export function handFingers(
  grips: readonly PhraseGrip[],
  startTick: number,
  endTick: number,
): ReadonlyMap<number, number> {
  const grip = grips.find(
    (candidate) => candidate.startTick < endTick && candidate.endTick > startTick,
  );

  if (grip === undefined) {
    return NO_FINGERS;
  }

  const fingers = new Map<number, number>();

  for (const assignment of grip.fingers) {
    if (!fingers.has(assignment.midi)) {
      fingers.set(assignment.midi, assignment.finger);
    }
  }

  return fingers;
}

/** Les colonnes de l'accord, dans l'ordre de la pile de la main droite. */
export function chordNoteColumns(input: {
  /**
   * Les touches de la main gauche, dans n'importe quel ordre.
   *
   * Elles ne peuvent qu'enrichir une colonne, jamais en ouvrir une : voir
   * « La main gauche n'ouvre plus la ligne ».
   */
  readonly left: readonly number[];
  readonly right: readonly number[];
  /**
   * L'orthographe de la fondamentale, qui décide des bémols et des dièses.
   *
   * L'analyseur écrit tantôt `Bb`, tantôt `B♭` : les deux glyphes disent la
   * même chose et mènent à la même table.
   */
  readonly rootSpelling: string;
  /** La classe de hauteur de la fondamentale, telle que l'analyseur la donne. */
  readonly rootPitchClass: number;
  /**
   * Les classes de hauteur que l'accord énonce, ou `null` s'il est illisible.
   *
   * Une colonne peut porter une note que l'accord ne nomme pas — la pile vient
   * de ce que les mains jouent, pas de la théorie. C'est alors la tonalité qui
   * l'épelle, sans quoi un `la` sous un mi♭ ressort en `si♭♭`. Voir
   * `windowToneName`.
   */
  readonly chordPitchClasses: ReadonlySet<number> | null;
  /** L'orthographe de la tonique du morceau. Voir `windowToneName`. */
  readonly keyTonic: string | null;
  /** Ce que l'analyseur a écrit, touche par touche. Voir `windowToneName`. */
  readonly spelling: ReadonlyMap<number, string> | null;
  readonly fingers: {
    readonly left: ReadonlyMap<number, number>;
    readonly right: ReadonlyMap<number, number>;
  };
}): ChordNoteColumn[] {
  const root = ((input.rootPitchClass % 12) + 12) % 12;
  const columns = new Map<number, ChordNoteColumn>();

  const classOf = (midi: number) => ((midi % 12) + 12) % 12;

  // La pile de la droite, qui **est** la ligne : son ordre est celui des
  // colonnes, et son contenu décide de quelles colonnes existent.
  const stack = [...new Set([...input.right].sort((a, b) => a - b).map(classOf))];

  // Les colonnes se remplissent des deux mains, mais une touche de la gauche
  // n'en crée jamais : elle rejoint celle de sa classe quand la droite la joue
  // aussi, et elle ne se montre pas sinon.
  const ordered = [...new Set([...input.left, ...input.right])]
    .filter((midi) => stack.includes(classOf(midi)))
    .sort((a, b) => a - b);

  for (const midi of ordered) {
    const pitchClass = classOf(midi);
    const existing = columns.get(pitchClass);
    const inLeft = input.left.includes(midi);
    const inRight = input.right.includes(midi);

    const left =
      existing?.left ??
      (inLeft ? { midi, finger: input.fingers.left.get(midi) ?? null } : null);
    const right =
      existing?.right ??
      (inRight ? { midi, finger: input.fingers.right.get(midi) ?? null } : null);

    columns.set(pitchClass, {
      pitchClass,
      root: pitchClass === root,
      name: windowToneName({
        spelled: input.spelling?.get(midi) ?? null,
        pitchClass,
        rootSpelling: input.rootSpelling,
        rootPitchClass: input.rootPitchClass,
        chordPitchClasses: input.chordPitchClasses,
        keyTonic: input.keyTonic,
      }),
      midis: existing === undefined ? [midi] : [...existing.midis, midi],
      left,
      right,
    });
  }

  return [...columns.values()].sort(
    (a, b) => stack.indexOf(a.pitchClass) - stack.indexOf(b.pitchClass),
  );
}
